import { useEffect, useMemo, useState } from "react";

import { GrowthUnifiedProfile } from "@/libraries/growth-unified-profile-client";
import { useGupsStatus } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks";

import { useLoggedInUser } from "../../../../utils";
import { useGeoLocation } from "../../../../utils/useGeoCurrency";
import { useWAEContext } from "../../../utils/wae-context-provider";

export enum UserSegmentTypes {
    LOGIN_STATUS = "login-status",
    GEOLOCATION = "geolocation",
    JOB_FUNCTION = "job-function",
    DOMAIN_TYPE = "domain-type",
    OWNED_PRODUCTS = "owned-products",
    CLUSTER_TAG = "cluster-tag"
}

export interface UserSegmentResponse {
    data?: UserSegmentValues;
    error?: unknown;
    status: "idle" | "fetching" | "complete";
}

export interface UserSegmentValues {
    [UserSegmentTypes.LOGIN_STATUS]?: "loggedIn" | "loggedOut";
    [UserSegmentTypes.GEOLOCATION]?: string;
    [UserSegmentTypes.JOB_FUNCTION]?: string;
    [UserSegmentTypes.DOMAIN_TYPE]?: string;
    [UserSegmentTypes.OWNED_PRODUCTS]?: string[];
    [UserSegmentTypes.CLUSTER_TAG]?: string;
}

// temporary implementation for testing purposes
export const useUserSegments = (): UserSegmentResponse => {
    const [status, setStatus] = useState<"idle" | "fetching" | "complete">(
        "idle"
    );
    const [profile, setProfile] = useState<GrowthUnifiedProfile>();
    const [profileError, setProfileError] = useState<unknown>(null);
    const loggedInUser = useLoggedInUser();
    const { personalizationValues } = useWAEContext();
    // Memoize the client to prevent recreation on every render
    const { getFullProfileCallback } = useGupsStatus();
    const envSource = typeof window !== "undefined" ? window : process.env;
    const { countryCode, status: locationStatus } = useGeoLocation(
        envSource["PRODUCT_CATALOG_LOCATION_BASE_URL"] as string
    );
    useEffect(() => {
        const getFullProfile = async () => {
            setStatus("fetching");
            const { data, error } = await getFullProfileCallback();
            setProfileError(error);
            setProfile(data);
            setStatus("complete");
        };

        getFullProfile();
    }, [getFullProfileCallback]);

    // Memoize the personalization values to prevent unnecessary recalculations
    return useMemo(() => {
        const products: string[] = [
            // de-duplicate product names
            ...new Set(
                profile?.sites?.flatMap(
                    (site) =>
                        site.products?.map(
                            (product) =>
                                product.productName || product.productKey
                        ) as string[]
                )
            )
        ].filter(Boolean);

        const personalizationFacetValue: UserSegmentValues = {
            ...personalizationValues,
            [UserSegmentTypes.LOGIN_STATUS]: loggedInUser.isUserLoggedIn
                ? "loggedIn"
                : "loggedOut",
            [UserSegmentTypes.GEOLOCATION]:
                locationStatus === "complete" ? countryCode : undefined,
            [UserSegmentTypes.JOB_FUNCTION]: profile?.userProfile?.jobFunction,
            [UserSegmentTypes.DOMAIN_TYPE]: profile?.userProfile?.domainType,
            [UserSegmentTypes.OWNED_PRODUCTS]: products
        };
        return {
            data: personalizationFacetValue,
            error: profileError,
            status
        };
    }, [
        loggedInUser.isUserLoggedIn,
        profile?.sites,
        profile?.userProfile?.domainType,
        profile?.userProfile?.jobFunction,
        profileError,
        countryCode,
        status,
        locationStatus,
        personalizationValues
    ]);
};
